/**
 * Sentry bootstrap for the consensus-v2 SPA.
 *
 * Opt-in via `VITE_SENTRY_DSN` (baked in at build time by Vite's `import.meta.env`
 * — there is no runtime secret here, DSNs are public-by-design, see
 * https://docs.sentry.io/product/sentry-basics/dsn-explainer/). Absence of the
 * env var is a hard no-op so local dev builds never phone home anywhere.
 *
 * `captureToSentry` is the single choke point wired into the pre-existing
 * `telemetryService.trackError` calls scattered across the transport/store
 * layer (SSE errors, wizard failures, TTS playback errors, etc.) — this
 * gives Sentry near-total coverage of "kaçak olmasın" without touching every
 * call site, mirroring the backend's stdlib `LoggingIntegration` approach.
 */
import * as Sentry from "@sentry/react";

let sentryActive = false;

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ?? "production",
    release: (import.meta.env.VITE_APP_VERSION as string | undefined) ?? undefined,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    // Debate transcripts/personas may contain sensitive operator input —
    // never auto-attach request/response bodies, and skip Session Replay
    // entirely (would otherwise record on-screen debate content).
    sendDefaultPii: false,
  });
  sentryActive = true;
}

/**
 * Forward an already-caught error to Sentry without throwing if Sentry
 * isn't initialized (dev / DSN-less builds) — mirrors `sentry_sdk`'s
 * no-op-when-uninitialized behaviour on the backend.
 */
export function captureToSentry(
  name: string,
  message?: string,
  stack?: string,
): void {
  if (!sentryActive) return;
  const err = new Error(message || name);
  if (stack) err.stack = stack;
  err.name = name;
  Sentry.captureException(err);
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;
